import { useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../auth/types';
import Logger from '../utils/logger';
import { AuthContext, supabase } from '../components/auth/AuthProvider';

export function useAuth() {
  const { session, isLoading, user: supabaseUser } = useContext(AuthContext);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [hasCheckedPermissions, setHasCheckedPermissions] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isAuthenticated = !!session;

  // Use a more stable cleanup function that doesn't depend on state
  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setHasCheckedPermissions(false);
  }, []);

  useEffect(() => {
    // Reset permissions state when session changes to null
    if (!session) {
      clearPermissions();
    }
  }, [session, clearPermissions]);

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAuthenticated && !hasCheckedPermissions) {
        try {
          // Extract JWT claims from the session token
          const jwt = session?.access_token || '';
          let userPermissions: string[] = [];

          // Check if token contains user role
          if (jwt) {
            try {
              // Parse JWT payload (second part of the token)
              const payload = JSON.parse(atob(jwt.split('.')[1]));

              // Check for user role in the token claims
              const userRole = payload.user_role === 'user';

              if (userRole) {
                userPermissions = ['jua:api'];
              }
            } catch (jwtError) {
              Logger.error('Failed to parse JWT token', jwtError);
            }
          }
          setPermissions(userPermissions);
        } catch (error) {
          Logger.error('Failed to get permissions', error);
          setPermissions([]);
          setError(error instanceof Error ? error : new Error('Unknown error'));
        }
        setHasCheckedPermissions(true);
      }
    };

    checkPermissions();
  }, [isAuthenticated, hasCheckedPermissions, session]);

  const logout = useCallback(async () => {
    try {
      Logger.info('User logging out, clearing auth state');

      // Clear permissions before signOut to avoid potential loops
      clearPermissions();

      // This should only be called once per logout request
      const response = await supabase.auth.signOut();

      if (response.error) {
        Logger.error('Logout failed', response.error);
      }

      // Navigate to login page is handled by the auth state change listener
    } catch (error) {
      Logger.error('Logout failed', error);
      setError(error instanceof Error ? error : new Error('Unknown error'));
    }
  }, [clearPermissions]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission);
    },
    [permissions],
  );

  // Check if user needs to complete profile
  const needsProfileCompletion = useCallback((): boolean => {
    if (!supabaseUser) return false;

    const displayName =
      supabaseUser.user_metadata?.full_name ||
      supabaseUser.user_metadata?.name ||
      supabaseUser.user_metadata?.display_name;
    const country = supabaseUser.user_metadata?.country;
    const howFoundUs = supabaseUser.user_metadata?.how_found_us;
    const userType = supabaseUser.user_metadata?.user_type;
    const companyName = supabaseUser.user_metadata?.company_name;
    const roleDescription = supabaseUser.user_metadata?.role_description;

    const hasDisplayName = Boolean(displayName && displayName.trim());
    const hasCountry = Boolean(country && country.trim());
    const hasHowFoundUs = Boolean(howFoundUs && howFoundUs.trim());
    const hasUserType = Boolean(userType && userType.trim());
    const hasRoleDescription = Boolean(roleDescription && roleDescription.trim());

    // For business users, company name is also required
    const hasCompanyName = userType !== 'business' || Boolean(companyName && companyName.trim());

    return (
      !hasDisplayName ||
      !hasCountry ||
      !hasHowFoundUs ||
      !hasUserType ||
      !hasCompanyName ||
      !hasRoleDescription
    );
  }, [supabaseUser]);

  const user: User | null = supabaseUser
    ? {
        sub: supabaseUser.id,
        email: supabaseUser.email || '',
        // Get display name from user_metadata first, then try identities data, finally fallback to email
        name:
          supabaseUser.user_metadata?.full_name ||
          supabaseUser.user_metadata?.name ||
          supabaseUser.user_metadata?.display_name ||
          supabaseUser.identities?.[0]?.identity_data?.full_name ||
          supabaseUser.identities?.[0]?.identity_data?.name ||
          supabaseUser.email ||
          'User',
        permissions,
      }
    : null;

  // Function to refresh user data after profile updates
  const refreshUser = useCallback(async () => {
    try {
      const {
        data: { user: refreshedUser },
      } = await supabase.auth.getUser();
      // The auth context will automatically update with the new user data
      return refreshedUser;
    } catch (error) {
      Logger.error('Failed to refresh user data', error);
      return null;
    }
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    logout,
    hasPermission,
    isPermissionsLoaded: hasCheckedPermissions,
    needsProfileCompletion,
    refreshUser,
  };
}
